import { AsyncPipe } from '@angular/common';
import { Component, inject, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DeviceStoreActions } from '@era/stores';
import { Store } from '@ngrx/store';
import { DeviceTask } from '@rocksoft/device-task-management-sdk';
import { SyncType } from '@rocksoft/apple-sync-sdk';
import { AuthorizationService, Permissions } from '@rocksoft/shared/authorization';
import { combineLatest, map, Observable } from 'rxjs';
import { DeviceScheduledTasksStoreSelectors } from '../../sub-features/device-sync/device-scheduled-tasks-store';
import { ForceSyncService } from '../device-mdm-sync/device-mdm-sync-force/device-mdm-sync-force.service';
import { DeviceSyncStatusRetryComponent } from './device-sync-status-retry.component';

@Component({
  selector: 'era-device-sync-status-retry',
  templateUrl: './device-sync-status-retry.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DeviceSyncStatusRetryComponent, AsyncPipe],
})
export class DeviceSyncStatusRetryContainerComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly forceSyncService = inject(ForceSyncService);

  SyncType = SyncType;
  private forceSync = false;

  public readonly currentSyncType = input.required<SyncType>();
  public allowRetry$: Observable<boolean>;
  private currentType: string;
  private readonly scheduledTasks$: Observable<DeviceTask[]>;
  private readonly inQueue$: Observable<boolean>;
  private readonly isAdmin$: Observable<boolean>;

  constructor() {
    this.scheduledTasks$ = this.store.select(DeviceScheduledTasksStoreSelectors.selectScheduledTasksByUdid);
    this.inQueue$ = this.scheduledTasks$.pipe(map((data) => data.some((x) => x.task === this.currentType)));
    this.isAdmin$ = this.authorizationService.getIsAuthorized$(Permissions.Admin);
  }

  ngOnInit() {
    this.forceSyncService.forceSync$.subscribe((value) => {
      this.forceSync = value;
    });

    this.currentType = this.convertSyncType(this.currentSyncType());

    if (this.currentSyncType() === SyncType.WifiProfiles) {
      this.allowRetry$ = combineLatest([this.isAdmin$, this.inQueue$]).pipe(
        map(([isAdmin, inQueue]) => isAdmin && !inQueue)
      );
    } else {
      this.allowRetry$ = this.inQueue$.pipe(map((data) => !data));
    }
  }

  public scheduleTask(event: SyncType): void {
    this.store.dispatch(
      DeviceStoreActions.DeviceSyncStatusViewActions.retryGivenSync({
        syncType: event,
        forceSync: this.forceSync,
      })
    );
  }

  private convertSyncType(syncType: SyncType): string {
    switch (syncType) {
      case SyncType.AirplaySecurityProfile:
        return 'airplay-sync';
      case SyncType.AppLockProfile:
        return 'applock-sync';
      case SyncType.Applications:
        return 'application-sync';
      case SyncType.ConfigurationProfiles:
        return 'configuration-sync';
      case SyncType.DeviceName:
        return 'devicename-sync';
      case SyncType.EraApplicationConfiguration:
        return 'application-configurations-sync';
      case SyncType.NotificationProfiles:
        return 'notification-sync';
      case SyncType.RestrictionsProfiles:
        return 'restrictions-sync';
      case SyncType.WifiProfiles:
        return 'wifi-sync';
      default:
        return '';
    }
  }
}
